import React from 'react';
import {IPropsInjectedByViewerScript} from '../types/app-types';
import {IHostProps, ISantaProps} from '@wix/native-components-infra/dist/es/src/types/types';
import {BackInStockStoreProps} from '../viewerScript/stores/BackInStockStore';
import {useStyles} from '@wix/tpa-settings/react';

export const PropsContext = React.createContext<ProvidedGlobalProps>(undefined);

export interface ProvidedGlobalProps {
  globals?: GlobalProps;
}

export type GlobalProps = Pick<
  IPropsInjectedByViewerScript,
  | 'addedToCartStatus'
  | 'addToCartState'
  | 'breakdownParams'
  | 'reportBI'
  | 'closeWixModal'
  | 'cashierExpressCheckoutWidgetProps'
  | 'cashierExpressEstimatedTotal'
  | 'currentPrice'
  | 'dynamicPaymentsMethodsAmount'
  | 'dynamicPaymentMethodsEnabled'
  | 'errorPlacement'
  | 'errors'
  | 'experiments'
  | 'handleAddToCart'
  | 'handleBuyNow'
  | 'handleOpenModal'
  | 'handleSubscribe'
  | 'handleUserInput'
  | 'handleWishlistButtonClick'
  | 'hasMultipleMedia'
  | 'hideNavigationUrls'
  | 'htmlTags'
  | 'infoSection'
  | 'isDesktop'
  | 'isEditorMode'
  | 'isEditorX'
  | 'isInteractive'
  | 'isMobile'
  | 'isAllowedToCheckout'
  | 'isProductSubmitted'
  | 'isQuickView'
  | 'isRTL'
  | 'isSEO'
  | 'isSSR'
  | 'isPreOrderState'
  | 'layoutId'
  | 'modalState'
  | 'navigate'
  | 'navigateToLink'
  | 'onAppLoaded'
  | 'pagePath'
  | 'pagination'
  | 'product'
  | 'productUrl'
  | 'productWasAddedToWishlist'
  | 'quantityRange'
  | 'remainingItemCount'
  | 'resetAddedToCartStatus'
  | 'resetWishlistStatus'
  | 'selectedVariant'
  | 'setDynamicPaymentsMethodsAmount'
  | 'shouldFocusAddToCartButton'
  | 'shouldShowAddToCartButton'
  | 'shouldShowAddToCartSuccessAnimation'
  | 'shouldShowBuyNowButton'
  | 'shouldShowProductPaymentBreakdown'
  | 'shouldShowDiscountName'
  | 'shouldShowSubscribeButton'
  | 'shouldShowSubscriptionPlans'
  | 'shouldShowWishlistButton'
  | 'shouldShorUserInputs'
  | 'slots'
  | 'dynamicPaymentMethodsTheme'
  | 'siteUrl'
  | 'socialSharing'
  | 'subscriptionPlans'
  | 'priceBreakdown'
  | 'texts'
  | 'userInputErrors'
  | 'userInputs'
  | 'validate'
  | 'variantInfo'
  | 'instanceId'
  | 'wishlistActionStatus'
  | 'withModalGallery'
  | 'styles'
  | keyof BackInStockStoreProps
> &
  Pick<IHostProps, 'updateLayout' | 'dimensions' | 'style' | 'accessibilityEnabled' | 'LazySentry'>;

export function withGlobalPropsProvider(Component) {
  // eslint-disable-next-line react/display-name
  return (props: IPropsInjectedByViewerScript & ISantaProps & {style: IHostProps['style']}) => {
    const {
      addToCartState,
      addedToCartStatus,
      breakdownParams,
      reportBI,
      cashierExpressCheckoutWidgetProps,
      cashierExpressEstimatedTotal,
      closeWixModal,
      currentPrice,
      dynamicPaymentsMethodsAmount,
      dynamicPaymentMethodsEnabled,
      errorPlacement,
      errors,
      experiments,
      handleAddToCart,
      handleBuyNow,
      handleNotifyWhenBackInStock,
      handleOpenModal,
      handleSubscribe,
      handleUserInput,
      handleWishlistButtonClick,
      hasMultipleMedia,
      hideNavigationUrls,
      htmlTags,
      infoSection,
      isBackInStockEnabled,
      isBackInStockLoading,
      isDesktop,
      isEditorMode,
      isEditorX,
      isInteractive,
      isMobile,
      isAllowedToCheckout,
      isProductSubmitted,
      isQuickView,
      isRTL,
      isSEO,
      isSSR,
      isPreOrderState,
      layoutId,
      modalState,
      navigate,
      navigateToLink,
      dynamicPaymentMethodsTheme,
      onAppLoaded,
      pagePath,
      pagination,
      product,
      productUrl,
      productWasAddedToWishlist,
      quantityRange,
      remainingItemCount,
      resetAddedToCartStatus,
      resetWishlistStatus,
      selectedVariant,
      setDynamicPaymentsMethodsAmount,
      shouldFocusAddToCartButton,
      shouldShowAddToCartButton,
      shouldShowAddToCartSuccessAnimation,
      shouldShowBuyNowButton,
      shouldShowProductPaymentBreakdown,
      shouldShowDiscountName,
      shouldShowSubscribeButton,
      shouldShowSubscriptionPlans,
      shouldShowWishlistButton,
      shouldShorUserInputs,
      siteUrl,
      socialSharing,
      style,
      subscriptionPlans,
      priceBreakdown,
      texts,
      userInputErrors,
      userInputs,
      validate,
      variantInfo,
      wishlistActionStatus,
      withModalGallery,
      shouldShowBackInStockButton,
      instanceId,
      ...passThroughProps
    } = props;

    const {dimensions, updateLayout, accessibilityEnabled, LazySentry} = props.host;
    const styles = useStyles();

    const globals = {
      accessibilityEnabled,
      addToCartState,
      addedToCartStatus,
      breakdownParams,
      reportBI,
      cashierExpressCheckoutWidgetProps,
      cashierExpressEstimatedTotal,
      closeWixModal,
      currentPrice,
      dimensions,
      dynamicPaymentsMethodsAmount,
      dynamicPaymentMethodsEnabled,
      errorPlacement,
      errors,
      experiments,

      handleAddToCart,
      handleBuyNow,
      handleNotifyWhenBackInStock,
      handleOpenModal,
      handleSubscribe,
      handleUserInput,
      handleWishlistButtonClick,
      hasMultipleMedia,
      hideNavigationUrls,
      htmlTags,
      infoSection,
      isBackInStockEnabled,
      isBackInStockLoading,
      isDesktop,
      isEditorMode,
      isEditorX,
      isInteractive,
      isMobile,
      isAllowedToCheckout,
      isProductSubmitted,
      isQuickView,
      isRTL,
      isSEO,
      isSSR,
      isPreOrderState,
      layoutId,
      modalState,
      navigate,
      navigateToLink,
      onAppLoaded,
      pagePath,
      pagination,
      product,
      productUrl,
      productWasAddedToWishlist,
      quantityRange,
      remainingItemCount,
      resetAddedToCartStatus,
      resetWishlistStatus,
      selectedVariant,
      setDynamicPaymentsMethodsAmount,
      shouldFocusAddToCartButton,
      shouldShowAddToCartButton,
      shouldShowAddToCartSuccessAnimation,
      shouldShowBuyNowButton,
      shouldShowProductPaymentBreakdown,
      shouldShowDiscountName,
      shouldShowSubscribeButton,
      shouldShowSubscriptionPlans,
      shouldShowWishlistButton,
      shouldShorUserInputs,
      dynamicPaymentMethodsTheme,
      siteUrl,
      slots: passThroughProps.slots,
      socialSharing,
      style,
      styles,
      LazySentry,
      subscriptionPlans,
      priceBreakdown,
      texts,
      updateLayout,
      userInputErrors,
      userInputs,
      validate,
      variantInfo,
      instanceId,
      wishlistActionStatus,
      withModalGallery,
      shouldShowBackInStockButton,
    };

    return (
      <PropsContext.Provider value={{globals}}>
        <Component {...passThroughProps} />
      </PropsContext.Provider>
    );
  };
}

export function withGlobalProps<T>(Component: any): React.FunctionComponent<T> | any {
  // eslint-disable-next-line react/display-name
  return (props) => {
    return <PropsContext.Consumer>{(globalProps) => <Component {...globalProps} {...props} />}</PropsContext.Consumer>;
  };
}
